(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    if (root.RealmHome) { root.RealmHome.mount(root.document, root); return; }
    root.RealmHome = api;
    const start = () => api.mount(root.document, root);
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})(typeof window === 'undefined' ? globalThis : window, function () {
  'use strict';
  function bindHome(home, document, window) {
    if (home.dataset.homeBound) return;
    home.dataset.homeBound = 'true';
    home.dataset.homeHistoryVersion = 'bounded-return-v2';
    let destroyed = false, editorHeroPaused = false;
    const pendingFrames = new Set(), removals = [];
    const slides = Array.from(home.querySelectorAll('[data-hero-slide]'));
    const hero = home.querySelector('[data-hero-carousel]');
    const heroTitle = home.querySelector('h1[data-hero-title]');
    const heroActions = Array.from(home.querySelectorAll('[data-hero-action]'));
    const heroButtons = Array.from(home.querySelectorAll('[data-hero-select]'));
    const list = home.querySelector('[data-home-products]');
    const thumbnailList = home.querySelector('[data-home-thumbnails]');
    const thumbnails = Array.from(home.querySelectorAll('[data-home-card-select]'));
    const productSlides = Array.from(home.querySelectorAll('[data-home-product-index]'));
    const previousProduct = home.querySelector('[data-home-product-previous]');
    const nextProduct = home.querySelector('[data-home-product-next]');
    const productPosition = home.querySelector('[data-home-product-position]');
    const categories = Array.from(home.querySelectorAll('[data-category-story]'));
    const desktop = window.matchMedia('(min-width:768px)');
    const reduce = window.matchMedia('(prefers-reduced-motion:reduce)');
    let heroSwiper = null;
    let productSwiper = null;
    let thumbnailSwiper = null;
    let categoryObserver = null;
    let retryTimer = null;
    let retries = 0;
    let activeHero = 0;
    let activeProduct = 0;
    let restoringProduct = false;
    let historyWritable = !window.Shopify?.designMode && Boolean(window.history?.replaceState);
    let returnGuard = null;
    const stateKey = `realmHomeProduct:${home.dataset.homeStateKey || 'home'}`;
    const cards = list ? Array.from(list.querySelectorAll('.realm-product-card')) : [];
    const entryURL = () => (window.location.pathname || '/') + (window.location.search || '');
    function readProductEntry() {
      if (window.Shopify?.designMode) return null;
      if (!historyWritable) return null;
      const state = window.history?.state?.realmHomeProducts?.[stateKey];
      return state?.version === 1 && state.url === entryURL() && cards.some(card => card.dataset.realmProductHandle === state.handle) ? state : null;
    }
    let restoredState = readProductEntry();
    // Session storage is a compatibility fallback for older return entries or
    // browsers without history storage, not ownership of a new home visit.
    const navigationType = window.performance?.getEntriesByType?.('navigation')?.[0]?.type;
    if (!window.Shopify?.designMode && !restoredState && (!window.history?.replaceState || ['back_forward', 'reload'].includes(navigationType))) {
      try { restoredState = JSON.parse(window.sessionStorage?.getItem(stateKey) || 'null'); } catch (_) {}
    }
    if (restoredState?.handle) {
      const restoredIndex = cards.findIndex(card => card.dataset.realmProductHandle === restoredState.handle);
      if (restoredIndex >= 0) activeProduct = restoredIndex;
    }
    function saveProduct(index = activeProduct) {
      if (destroyed || window.Shopify?.designMode) return;
      if (restoringProduct || returnGuard) return;
      const handle = cards[index]?.dataset.realmProductHandle;
      if (!handle) return;
      const bodyStyle = document.body?.style;
      const fixed = bodyStyle?.getPropertyValue('position') === 'fixed';
      const offset = name => Math.max(0, -(Number.parseFloat(bodyStyle.getPropertyValue(name)) || 0));
      const state = {version: 1, url: entryURL(), handle, model: home.classList.contains('realm-model-view'),
        scrollX: fixed ? offset('left') : window.scrollX, scrollY: fixed ? offset('top') : window.scrollY, width: window.innerWidth};
      try {
        if (historyWritable) {
          window.history.replaceState({...window.history.state, realmHomeProducts: {...window.history.state?.realmHomeProducts, [stateKey]: state}}, '', window.location.href);
          window.history.scrollRestoration = 'manual';
        }
      } catch (_) { historyWritable = false; }
      try { window.sessionStorage?.setItem(stateKey, JSON.stringify(state)); } catch (_) {}
    }

    function updateHero(index) {
      activeHero = index;
      if (heroTitle && slides[index]) heroTitle.textContent = slides[index].dataset.heroTitle;
      heroActions.forEach(action => { action.hidden = Number(action.dataset.heroAction) !== index; });
      heroButtons.forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.heroSelect) === index)));
      syncHeroMotion();
    }

    function syncHeroMotion() {
      if (destroyed) return;
      const paused = reduce.matches;
      const interrupted = editorHeroPaused || !!window.RealmIntroduction?.active || document.visibilityState === 'hidden' || hero?.contains(document.activeElement) || document.querySelector?.('dialog[open]');
      if (heroSwiper) {
        if (paused) heroSwiper.autoplay?.stop();
        else if (interrupted) heroSwiper.autoplay?.pause();
        else if (slides.length > 1) {
          if (heroSwiper.autoplay?.running) heroSwiper.autoplay.resume();
          else heroSwiper.autoplay?.start();
        }
      }
      for (const video of hero?.querySelectorAll('video') || []) {
        const current = slides[activeHero]?.contains(video);
        if (paused || interrupted || !current) video.pause();
        else if (video.autoplay) video.play()?.catch?.(() => {});
      }
    }

    function updateProgress(value) {
      const progress = Math.max(0, Math.min(1, value));
      for (const bar of home.querySelectorAll('[data-hero-progress]')) {
        const index = Number(bar.dataset.heroProgress);
        bar.style.transform = `scaleX(${index < activeHero ? 1 : index === activeHero ? progress : 0})`;
      }
    }

    function updateThumbnails(index) {
      if (restoringProduct) return;
      // Late Swiper resize/on-load callbacks must not rewrite a returning
      // entry before the customer has interacted with the restored rail.
      if (returnGuard && index !== returnGuard.index) { scheduleReturn(); return; }
      activeProduct = Math.max(0, Math.min(Math.max(0, thumbnails.length - 1), index));
      thumbnails.forEach((button, buttonIndex) => button.setAttribute('aria-pressed', String(buttonIndex === activeProduct)));
      if (previousProduct) previousProduct.disabled = activeProduct === 0;
      if (nextProduct) nextProduct.disabled = activeProduct >= thumbnails.length - 1;
      if (productPosition) {
        productPosition.textContent = `${thumbnails.length ? activeProduct + 1 : 0} / ${thumbnails.length}`;
        productPosition.setAttribute('aria-label', productPosition.dataset.positionLabel.replace('__position__', String(activeProduct + 1)));
      }
      productSlides.forEach((slide, slideIndex) => {
        const offscreen = !desktop.matches && slideIndex !== activeProduct;
        slide.inert = offscreen;
        slide.setAttribute('aria-hidden', String(offscreen));
      });
      saveProduct();
    }
    function selectProduct(index) {
      index = Math.max(0, Math.min(Math.max(0, thumbnails.length - 1), index));
      if (productSwiper) {
        productSwiper.slideTo(index, reduce.matches ? 0 : 300);
        thumbnailSwiper?.slideTo(index, reduce.matches ? 0 : 300);
      } else if (list && productSlides[index]) {
        const left = productSlides[index].offsetLeft - (productSlides[0]?.offsetLeft || 0);
        if (list.scrollTo) list.scrollTo({left, behavior: reduce.matches ? 'auto' : 'smooth'});
        else list.scrollLeft = left;
      }
      updateThumbnails(index);
    }

    function initProducts() {
      if (!list) return;
      if (productSwiper) { productSwiper.update?.(); updateThumbnails(activeProduct); return; }
      if (!window.Swiper) { updateThumbnails(activeProduct); return; }
      if (thumbnailList) thumbnailSwiper = new window.Swiper(thumbnailList, {
        speed: reduce.matches ? 0 : 300,
        slidesPerView: 'auto',
        spaceBetween: 4,
        freeMode: true,
        watchOverflow: true,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        breakpoints: {768: {slidesPerView: 5}}
      });
      productSwiper = new window.Swiper(list, {
        speed: reduce.matches ? 0 : 300,
        noSwiping: true,
        noSwipingSelector: 'button,input,select,textarea,label,[data-realm-card-sizes],[data-realm-wishlist-toggle]',
        slidesPerView: 1,
        spaceBetween: 4,
        freeMode: false,
        watchOverflow: true,
        initialSlide: activeProduct,
        autoplay: false,
        breakpoints: {768: {slidesPerView: 2, spaceBetween: 4}, 1025: {slidesPerView: 3, spaceBetween: 4}},
        ...(thumbnailSwiper ? {thumbs: {multipleActiveThumbs: false, swiper: thumbnailSwiper}} : {}),
        on: {
          init: swiper => updateThumbnails(swiper.activeIndex || 0),
          slideChange: swiper => updateThumbnails(swiper.activeIndex || 0)
        }
      });
      if (list.scrollLeft) list.scrollLeft = 0;
    }

    function queueFrame(callback) {
      const id = window.requestAnimationFrame(() => { pendingFrames.delete(id); if (!destroyed) callback(); });
      pendingFrames.add(id); return id;
    }
    function listen(target, type, handler) { target.addEventListener(type, handler); removals.push(() => target.removeEventListener(type, handler)); }
    function revealCategory(category) {
      queueFrame(() => {
        queueFrame(() => {
          if (home.isConnected) category.classList.add('is-visible');
        });
      });
    }

    if (!reduce.matches && window.IntersectionObserver) {
      categoryObserver = new window.IntersectionObserver((entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          revealCategory(entry.target);
          observer.unobserve(entry.target);
        }
      }, {rootMargin: '0px 0px -18% 0px', threshold: .18});
      categories.forEach(category => {
        category.classList.add('is-animating');
        categoryObserver.observe(category);
        listen(category, 'focusin', () => {
          revealCategory(category);
          categoryObserver?.unobserve(category);
        });
      });
    } else categories.forEach(revealCategory);

    function initSwipers() {
      if (destroyed || !home.isConnected) return;
      if (!window.Swiper) {
        if (++retries <= 80) retryTimer = window.setTimeout(initSwipers, 100);
        return;
      }
      if (!heroSwiper && hero && slides.length) {
        slides.forEach(slide => { slide.hidden = false; });
        heroSwiper = new window.Swiper(home.querySelector('[data-hero-slider]'), {
          effect: 'fade', fadeEffect: {crossFade: true},
          initialSlide: activeHero,
          loop: slides.length > 1,
          allowTouchMove: slides.length > 1,
          speed: reduce.matches ? 0 : 700,
          autoplay: slides.length > 1 && !reduce.matches ? {delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true} : false,
          on: {
            init: swiper => { updateHero(swiper.realIndex || 0); updateProgress(0); },
            slideChange: swiper => { updateHero(swiper.realIndex || 0); updateProgress(0); },
            autoplayTimeLeft: (_swiper, _time, progress) => updateProgress(1 - progress)
          }
        });
        syncHeroMotion();
      }
      initProducts();
    }

    if (list) {
      thumbnails.forEach((button, index) => {
        const card = cards[index];
        const model = card?.querySelector('.realm-card-model');
        if (!model) return;
        const setModelReady = () => { card.dataset.realmModelReady = String(model.complete && model.naturalWidth > 0); };
        setModelReady();
        listen(model, 'load', setModelReady);
        listen(model, 'error', () => { card.dataset.realmModelReady = 'false'; });
        const thumbnailModel = model.cloneNode();
        thumbnailModel.className = 'realm-home-thumb-model';
        thumbnailModel.alt = '';
        thumbnailModel.sizes = '(min-width:768px) 76.8px, 86px';
        const setThumbnailReady = () => { button.dataset.homeModelReady = String(thumbnailModel.complete && thumbnailModel.naturalWidth > 0); };
        setThumbnailReady();
        listen(thumbnailModel, 'load', setThumbnailReady);
        listen(thumbnailModel, 'error', () => { button.dataset.homeModelReady = 'false'; });
        button.append(thumbnailModel);
        removals.push(() => thumbnailModel.remove());
      });
    }

    desktop.addEventListener('change', initProducts);
    function restoreProduct(state) {
      const savedIndex = state ? cards.findIndex(card => card.dataset.realmProductHandle === state.handle) : activeProduct;
      // Swiper.update can emit a transient slideChange(0) while the cached
      // geometry is refreshed. It must not overwrite this entry's caller card.
      restoringProduct = true;
      try {
        productSwiper?.update?.();
        productSwiper?.slideTo(savedIndex, 0);
        thumbnailSwiper?.slideTo(savedIndex, 0);
        if (!productSwiper && list && productSlides[savedIndex]) {
          const left = productSlides[savedIndex].offsetLeft - (productSlides[0]?.offsetLeft || 0);
          if (list.scrollTo) list.scrollTo({left, behavior: 'instant'});
          else list.scrollLeft = left;
        }
        if (state) {
          home.classList.toggle('realm-model-view', Boolean(state.model));
          home.querySelector('[data-home-model-toggle]')?.setAttribute('aria-checked', String(Boolean(state.model)));
        }
      } finally { restoringProduct = false; }
      updateThumbnails(savedIndex);
      if (state && Number.isFinite(state.scrollY) && state.scrollY >= 0 && state.width === window.innerWidth && window.scrollTo &&
          (Math.abs(window.scrollY - state.scrollY) > 1 || Math.abs(window.scrollX - (state.scrollX || 0)) > 1))
        window.scrollTo({left: state.scrollX || 0, top: state.scrollY, behavior: 'instant'});
    }
    function stopReturn() {
      if (!returnGuard) return;
      const guard = returnGuard; returnGuard = null;
      if (guard.frame !== null) window.cancelAnimationFrame?.(guard.frame);
      window.clearTimeout(guard.timer); guard.observer?.disconnect();
      for (const [style, value, priority] of guard.styles) {
        if (value) style.setProperty('overflow-anchor', value, priority);
        else style.removeProperty('overflow-anchor');
      }
    }
    function scheduleReturn() {
      const guard = returnGuard;
      if (!guard || guard.frame !== null) return;
      guard.frame = queueFrame(() => {
        if (returnGuard !== guard) return;
        guard.frame = null; restoreProduct(guard.state);
      });
    }
    function beginReturn(state) {
      const index = cards.findIndex(card => card.dataset.realmProductHandle === state?.handle);
      if (index < 0) return false;
      if (!readProductEntry() && historyWritable) {
        // Upgrade a legacy same-browser return without fabricating an offset
        // that its older record never captured.
        try { window.history.replaceState({...window.history.state, realmHomeProducts: {...window.history.state?.realmHomeProducts, [stateKey]: {...state, version: 1, url: entryURL()}}}, '', window.location.href); }
        catch (_) { historyWritable = false; }
      }
      stopReturn();
      const guard = returnGuard = {state: {...state}, index, frame: null, timer: null, observer: null, styles: []};
      for (const element of [document.documentElement, document.body]) {
        const style = element?.style;
        if (!style) continue;
        guard.styles.push([style, style.getPropertyValue('overflow-anchor'), style.getPropertyPriority('overflow-anchor')]);
        style.setProperty('overflow-anchor', 'none', 'important');
      }
      if (window.ResizeObserver) {
        guard.observer = new window.ResizeObserver(scheduleReturn);
        if (document.body) guard.observer.observe(document.body);
        if (list) guard.observer.observe(list);
      }
      guard.timer = window.setTimeout(stopReturn, 20000);
      restoreProduct(guard.state);
      return true;
    }
    const onPageShow = event => {
      const state = readProductEntry() || {handle: cards[activeProduct]?.dataset.realmProductHandle, model: home.classList.contains('realm-model-view')};
      const backward = event?.persisted || window.performance?.getEntriesByType?.('navigation')?.[0]?.type === 'back_forward';
      if (backward && beginReturn(state)) return;
      restoreProduct(state);
    };
    const onPageHide = () => { stopReturn(); saveProduct(); };
    const onReturnIntent = event => {
      if (event.isTrusted === false || (event.type === 'keydown' && ['Shift', 'Control', 'Alt', 'Meta'].includes(event.key))) return;
      stopReturn();
    };
    const onReturnScroll = () => {
      if (returnGuard && Number.isFinite(returnGuard.state.scrollY) && Math.abs(window.scrollY - returnGuard.state.scrollY) > 1) scheduleReturn();
    };
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('scroll', onReturnScroll, {passive: true});
    window.addEventListener('resize', stopReturn);
    for (const name of ['pointerdown', 'touchstart', 'wheel', 'keydown']) window.addEventListener(name, onReturnIntent, {capture: true, passive: name !== 'keydown'});
    const onProductScroll = () => {
      if (productSwiper || !productSlides.length) return;
      const step = productSlides.length > 1 ? productSlides[1].offsetLeft - productSlides[0].offsetLeft : list.clientWidth;
      if (step > 0) updateThumbnails(Math.round(list.scrollLeft / step));
    };
    list?.addEventListener('scroll', onProductScroll, {passive: true});
    function updateReducedMotion() {
      if (heroSwiper) {
        heroSwiper.params.speed = reduce.matches ? 0 : 700;
        if (!reduce.matches && slides.length > 1) {
          heroSwiper.params.autoplay = {...heroSwiper.params.autoplay, enabled: true, delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true};
        }
      }
      syncHeroMotion();
      if (productSwiper) productSwiper.params.speed = reduce.matches ? 0 : 300;
      if (thumbnailSwiper) thumbnailSwiper.params.speed = reduce.matches ? 0 : 300;
      if (reduce.matches) {
        categoryObserver?.disconnect();
        categories.forEach(revealCategory);
      }
    }
    reduce.addEventListener('change', updateReducedMotion);
    const onHeroFocusIn = () => syncHeroMotion();
    const onHeroFocusOut = event => { if (!hero.contains(event.relatedTarget)) queueFrame(syncHeroMotion); };
    const onOverlayOpening = () => { heroSwiper?.autoplay?.pause(); for (const video of hero?.querySelectorAll('video') || []) video.pause(); };
    hero?.addEventListener('focusin', onHeroFocusIn);
    hero?.addEventListener('focusout', onHeroFocusOut);
    document.addEventListener('visibilitychange', syncHeroMotion);
    document.addEventListener('realm:intro-released', syncHeroMotion);
    document.addEventListener('realm:overlay-opening', onOverlayOpening);
    document.addEventListener('close', syncHeroMotion, true);
    function selectedHeroBlock(event) {
      if (event.detail?.sectionId && String(event.detail.sectionId) !== home.dataset.homeSectionId) return -1;
      const direct = event.target?.closest?.('[data-hero-slide]');
      return slides.findIndex(slide => slide === direct || event.detail?.blockId && slide.dataset.heroBlockId === String(event.detail.blockId));
    }
    const onBlockSelect = event => {
      const index = selectedHeroBlock(event);
      if (index < 0) return;
      editorHeroPaused = true;
      if (heroSwiper) heroSwiper.slideToLoop(index, 0);
      else slides.forEach((slide, number) => { slide.hidden = number !== index; });
      updateHero(index); syncHeroMotion();
    };
    const onBlockDeselect = event => { if (selectedHeroBlock(event) === activeHero) { editorHeroPaused = false; syncHeroMotion(); } };
    if (hero) { listen(document, 'shopify:block:select', onBlockSelect); listen(document, 'shopify:block:deselect', onBlockDeselect); }
    home._realmHomeCleanup = () => {
      if (destroyed) return;
      destroyed = true;
      for (const id of pendingFrames) window.cancelAnimationFrame(id);
      pendingFrames.clear();
      for (const remove of removals) remove();
      delete home.dataset.homeBound;
      delete home._realmHomeCleanup;
      stopReturn();
      if (retryTimer) window.clearTimeout(retryTimer);
      heroSwiper?.destroy(true, true);
      productSwiper?.destroy(true, true);
      thumbnailSwiper?.destroy(true, true);
      categoryObserver?.disconnect();
      desktop.removeEventListener('change', initProducts);
      reduce.removeEventListener('change', updateReducedMotion);
      hero?.removeEventListener('focusin', onHeroFocusIn);
      hero?.removeEventListener('focusout', onHeroFocusOut);
      document.removeEventListener('visibilitychange', syncHeroMotion);
      document.removeEventListener('realm:intro-released', syncHeroMotion);
      document.removeEventListener('realm:overlay-opening', onOverlayOpening);
      document.removeEventListener('close', syncHeroMotion, true);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('scroll', onReturnScroll);
      window.removeEventListener('resize', stopReturn);
      for (const name of ['pointerdown', 'touchstart', 'wheel', 'keydown']) window.removeEventListener(name, onReturnIntent, {capture: true});
      list?.removeEventListener('scroll', onProductScroll);
      heroSwiper = null;
      productSwiper = null;
      thumbnailSwiper = null;
    };
    // A cold history return may initialize before pageshow. Protect its saved
    // offset/card before any library init callback can write the new geometry.
    if (navigationType === 'back_forward' && restoredState) beginReturn(restoredState);
    initSwipers();
    syncHeroMotion();
    updateThumbnails(activeProduct);
    if (restoredState?.model) {
      home.classList.toggle('realm-model-view', true);
      home.querySelector('[data-home-model-toggle]')?.setAttribute('aria-checked', 'true');
      saveProduct();
    }
    if (!productSwiper && activeProduct) selectProduct(activeProduct);
    listen(home, 'click', event => {
      if (event.target.closest('[data-home-product-previous]')) selectProduct(activeProduct - 1);
      if (event.target.closest('[data-home-product-next]')) selectProduct(activeProduct + 1);
      const productLink = event.target.closest('[data-home-products] a[href]');
      if (productLink) {
        const slide = productLink.closest('[data-home-product-index]');
        if (slide) saveProduct(Number(slide.dataset.homeProductIndex));
      }
      const heroButton = event.target.closest('[data-hero-select]');
      if (heroButton) {
        const index = Number(heroButton.dataset.heroSelect);
        if (heroSwiper) heroSwiper.slideToLoop(index, reduce.matches ? 0 : 700);
        else {
          slides.forEach(slide => { slide.hidden = Number(slide.dataset.heroSlide) !== index; });
          updateHero(index);
        }
      }
      const modelButton = event.target.closest('[data-home-model-toggle]');
      if (modelButton) {
        const showModel = modelButton.getAttribute('aria-checked') !== 'true';
        modelButton.setAttribute('aria-checked', String(showModel));
        home.classList.toggle('realm-model-view', showModel);
        saveProduct();
      }
      const thumbnail = event.target.closest('[data-home-card-select]');
      if (thumbnail) {
        const cards = list?.querySelectorAll('.realm-product-card');
        const card = cards?.[Number(thumbnail.dataset.homeCardSelect)];
        if (!card || !list) return;
        selectProduct(Number(thumbnail.dataset.homeCardSelect));
      }
    });
  }
  const installed = new WeakSet();
  function homesIn(scope) {
    return [...(scope.matches?.('[data-realm-home]') ? [scope] : []), ...scope.querySelectorAll('[data-realm-home]')];
  }
  function mount(document, window, scope = document) {
    for (const home of homesIn(scope)) bindHome(home, document, window);
    if (installed.has(document)) return;
    installed.add(document);
    document.addEventListener('shopify:section:load', event => mount(document, window, event.target));
    document.addEventListener('shopify:section:unload', event => { for (const home of homesIn(event.target)) home._realmHomeCleanup?.(); });
  }
  return { bindHome, mount };
});
